
import React from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Container,
    Button,
    Chip,
    useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

const itrOptions = [
    {
        title: "ITR-1",
        subtitle: "Simple Income Tax Return",
        desc: "Applicable for income from salary, one house property, family pension, agricultural income up to ₹50,000 and LTCG under Section 112A up to ₹1.25 lakhs.",
        eligibility: "Eligible Individuals",
        features: [
            "Salary / Pension",
            "One House Property",
            "Other Permitted Income",
        ],
    },
    {
        title: "ITR-2",
        subtitle: "For Multiple Income Sources",
        desc: "Applicable for income from salary or pension, multiple house properties, capital gains, other income sources and agricultural income exceeding ₹5,000.",
        eligibility: "Individuals & HUFs",
        features: [
            "Capital Gains",
            "Multiple House Properties",
            "Foreign Assets / Income",
        ],
    },
];

const ITR = () => {
    const theme = useTheme();
    const MotionBox = motion(Box);

    const handleContact = () => {
        const message =
            "Hi, I’m interested in ITR filing. Please share the process and pricing.";

        const whatsappUrl =
            `https://wa.me/918688039761?text=${encodeURIComponent(message)}`;

        window.open(whatsappUrl, "_blank", "noopener,noreferrer");
    };

    return (
        <Box
            id="itr"
            sx={{
                py: { xs: 5, md: 9 },
                background: `linear-gradient(
                    180deg,
                    ${theme.palette.background.default} 0%,
                    ${theme.palette.background.paper} 100%
                )`,
            }}
        >
            <Container maxWidth="lg">
                {/* HEADER */}
                <Box
                    sx={{
                        textAlign: "center",
                        mb: { xs: 4, md: 6 },
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "13px",
                            letterSpacing: "1.5px",
                            fontWeight: 600,
                            mb: 1,
                            color: theme.palette.primary.main,
                        }}
                        data-aos="fade-right"
                    >
                        • INCOME TAX SERVICES
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: { xs: "20px", sm: "28px", md: "40px" },
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            position: "relative",
                            display: "inline-block",
                        }}
                    >
                        File Your ITR with{" "}
                        <Box
                            component="span"
                            sx={{
                                fontWeight: 900,
                                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.text.primary})`,
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                position: "relative",
                                display: "inline-block",
                                "&::after": {
                                    content: '""',
                                    position: "absolute",
                                    bottom: "-4px",
                                    left: 0,
                                    width: "100%",
                                    height: "2px",
                                    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, transparent 100%)`,
                                }
                            }}
                        >
                            Confidence
                        </Box>
                    </Typography>

                    <Typography
                        sx={{
                            mt: 2,
                            maxWidth: 690,
                            mx: "auto",
                            fontSize: { xs: 14, md: 16 },
                            color: theme.palette.text.secondary,
                            lineHeight: 1.8,
                        }}
                    >
                        Understand different ITR forms and find the right
                        return form based on your income sources and
                        eligibility. Make your tax filing journey simpler
                        with ArthGyan.
                    </Typography>
                </Box>

                {/* ITR CARDS */}
                <MotionBox
                    initial={{ y: 35, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    viewport={{
                        once: true,
                        amount: 0.15,
                    }}
                    transition={{ duration: 0.6 }}
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, minmax(0, 1fr))",
                        },
                        gap: { xs: 2.5, md: 3 },
                        alignItems: "stretch",
                    }}
                >
                    {itrOptions.map((item) => (
                        <Card
                            key={item.title}
                            elevation={0}
                            sx={{
                                height: "100%",
                                borderRadius: theme.shape.borderRadius / 4,
                                border: `1px solid ${theme.palette.divider}`,
                                backgroundColor: theme.palette.background.paper,
                                transition: "all 0.3s ease",
                                overflow: "hidden",
                                "&:hover": {
                                    transform: "translateY(-5px)",
                                    borderColor: theme.palette.primary.light,
                                    boxShadow: `0 18px 40px ${theme.palette.primary.main}1A`,
                                },
                            }}
                        >
                            <CardContent
                                sx={{
                                    p: { xs: 2.5, md: 3 },
                                    "&:last-child": {
                                        pb: 3,
                                    },
                                }}
                            >
                                {/* ICON AND ELIGIBILITY */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        justifyContent: "space-between",
                                        gap: 2,
                                        mb: 2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 52,
                                            height: 52,
                                            flexShrink: 0,
                                            borderRadius: 2,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: theme.palette.primary.main,
                                            backgroundColor:
                                                theme.palette.secondary.main,
                                        }}
                                    >
                                        <AssignmentOutlinedIcon
                                            sx={{ fontSize: 29 }}
                                        />
                                    </Box>

                                    <Chip
                                        label={item.eligibility}
                                        size="small"
                                        sx={{
                                            maxWidth: "70%",
                                            height: "auto",
                                            color: theme.palette.primary.dark,
                                            backgroundColor:
                                                theme.palette.secondary.main,
                                            fontWeight: 600,
                                            "& .MuiChip-label": {
                                                py: 0.7,
                                                whiteSpace: "normal",
                                            },
                                        }}
                                    />
                                </Box>

                                {/* TITLE */}
                                <Typography
                                    component="h3"
                                    sx={{
                                        fontSize: 23,
                                        fontWeight: 800,
                                        color: theme.palette.text.primary,
                                        mb: 0.5,
                                    }}
                                >
                                    {item.title}
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: 14,
                                        fontWeight: 600,
                                        color: theme.palette.primary.main,
                                        mb: 1.5,
                                    }}
                                >
                                    {item.subtitle}
                                </Typography>

                                {/* DESCRIPTION */}
                                <Typography
                                    sx={{
                                        fontSize: 13.5,
                                        color: theme.palette.text.secondary,
                                        lineHeight: 1.8,
                                        mb: 2.5,
                                    }}
                                >
                                    {item.desc}
                                </Typography>

                                {/* FEATURES */}
                                <Box
                                    sx={{
                                        borderTop: `1px solid ${theme.palette.divider}`,
                                        pt: 2,
                                        mb: 2.5,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            fontWeight: 700,
                                            color: theme.palette.text.primary,
                                            mb: 1.5,
                                        }}
                                    >
                                        KEY INCOME CATEGORIES
                                    </Typography>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 1,
                                        }}
                                    >
                                        {item.features.map((feature) => (
                                            <Chip
                                                key={feature}
                                                label={feature}
                                                size="small"
                                                variant="outlined"
                                                sx={{
                                                    fontSize: 11,
                                                    color: theme.palette.text.secondary,
                                                    borderColor: theme.palette.divider,
                                                    borderRadius: 1.5,
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </Box>



                            </CardContent>
                        </Card>
                    ))}
                </MotionBox>


                {/* ITR SERVICE BENEFITS */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: { xs: 1.5, md: 4 },
                        mt: { xs: 4, md: 5 },
                        mb: 1,
                    }}
                >
                    {[
                        "Expert CA Assisted Filing",
                        "Priority Processing",
                        "ITR Filing Within 24–48 Hours",
                    ].map((feature) => (
                        <Box
                            key={feature}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <CheckCircleRoundedIcon
                                sx={{
                                    fontSize: 19,
                                    color: "#16A34A",
                                }}
                            />

                            <Typography
                                sx={{
                                    fontSize: { xs: 12, sm: 13 },
                                    fontWeight: 600,
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                {feature}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* BOTTOM CTA */}
                <Box
                    sx={{
                        mt: { xs: 4, md: 6 },
                        p: { xs: 3, md: 4 },
                        borderRadius: 3,
                        textAlign: "center",
                        background: `linear-gradient(
                            110deg,
                            ${theme.palette.primary.dark},
                            ${theme.palette.primary.main}
                        )`,
                        color: theme.palette.primary.contrastText,
                    }}
                >
                    <Typography
                        component="h3"
                        sx={{
                            fontSize: { xs: 21, md: 28 },
                            fontWeight: 800,
                            mb: 1,
                        }}
                    >
                        Need Help with Your ITR?
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: 14,
                            lineHeight: 1.8,
                            mb: 2.5,
                            opacity: 0.95,
                        }}
                    >
                        Explore your tax filing options and understand
                        which return form may apply to you.
                    </Typography>

                    <Button
                        variant="contained"
                        endIcon={<ArrowForwardRoundedIcon />}
                        onClick={handleContact}
                        sx={{
                            px: 3,
                            py: 1.2,
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 700,
                            color: theme.palette.primary.main,
                            backgroundColor: theme.palette.background.paper,
                            "&:hover": {
                                backgroundColor: theme.palette.secondary.main,
                            },
                        }}
                    >
                        Contact ArthGyan
                    </Button>
                </Box>
            </Container>
        </Box>
    );
};

export default ITR;
